import { Button, Checkbox, Input, message, Select, Table, Tag, Tooltip, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { DownOutlined, RightOutlined } from "@ant-design/icons";

import { controller as breakdownController } from "../TreatmentPlans/controller";
import { describeApiError } from "../TreatmentPlans/lib/apiError";
import AddPatientModal from "./AddPatientModal";

import arrowDown from "../../assets/icons/arrow-down12.png";
import tick from "../../assets/icons/tick-circle-gray.svg";
import close from "../../assets/icons/close.svg";
import trash from "../../assets/icons/trash.png";
import edit from "../../assets/icons/edit-2.svg";
import { NOTE_TOOLTIP_PROPS } from "../TreatmentPlans/lib/noteTooltip";

// `patients` are the patients that extracted from uploaded files, `allPatients` is the all office patients.
export default function InsuranceBreakDownProceduresTable({ tableData, setTableData, allProcedures, selectedBreakdown, getBreakdownsDetailsData }) {
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [recordsToEdit, setRecordsToEdit] = useState([]); // To manage the records that we want to edit
  const [editedData, setEditedData] = useState({}); // { [recordId]: { procedure_code, procedure_name, coverage, overall_max, max_occurrences, coverage_duration_months, notes, isUnlimited, isFrequencyUnlimited } }
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownProceduresOptions, setDropdownProceduressOptions] = useState([]);
  const [isDeletingBreakdownProcedure, setIsDeletingBreakdownProcedure] = useState(false);
  const [isEditingProcedure, setIsEditingProcedure] = useState(false); // Loading for editing whole procedure
  const [showUnavailable, setShowUnavailable] = useState(false); // The collapsed "data not available" section

  // SECTION: useEff
  // INFO: This sets the unused procedures to in the proceudre dropdown
  useEffect(function () {
    const breakdownProceduresCode = tableData.map((breakdown) => breakdown.procedure_code);

    const availableProcedures = allProcedures
      .filter((procedure) => !breakdownProceduresCode.includes(procedure.id))
      .map((procedure) => ({
        value: procedure.id,
        label: `${procedure.procedure_code} ${procedure.procedure_code_description ? `- ${procedure.procedure_code_description}` : ""}`,
      }));

    setDropdownProceduressOptions(availableProcedures);
  }, []);

  const breakdownColumnTitle = (text) => (
    <Tooltip title={text}>
      <Typography.Text ellipsis={{ tooltip: false }} style={{ display: "block", maxWidth: "100%" }}>
        {text}
      </Typography.Text>
    </Tooltip>
  );

  const patientInsurancesDetailsColumns = [
    {
      title: breakdownColumnTitle("Procedures"),
      key: "procedure",
      width: recordsToEdit.length > 0 ? 202 : 210,
      render: (_, record) => {
        const { id, procedure_code, description } = record.procedure_code_details;

        if (recordsToEdit.includes(record.id)) {
          const currentEdit = editedData[record.id] || {};

          return (
            <Select
              showSearch
              placeholder="Select Procedure"
              optionFilterProp="label"
              className="edit-breakdown-procedure-cell"
              options={dropdownProceduresOptions}
              style={{ width: "100%" }}
              value={
                currentEdit.procedure_code
                  ? `${currentEdit.procedure_code} ${currentEdit.procedure_description ? `- ${currentEdit.procedure_description}` : ""}`
                  : `${procedure_code} ${description ? `- ${description}` : ""}`
              }
              suffixIcon={<img style={{ transform: `rotate(${isOpen ? "180deg" : "0"})`, width: 20 }} src={arrowDown} alt="arrow-down" />}
              onOpenChange={(open) => setIsOpen(open)}
              onChange={(value) => handleChangeProcedureCode(record.id, value)}
              disabled={isEditingProcedure}
            />
          );
        }

        if (!id) return "-";
        const procedureLabel = `${procedure_code} ${description ? `- ${description}` : ""}`;
        return (
          <Tooltip title={procedureLabel}>
            <Typography.Text ellipsis={{ tooltip: false }} style={{ maxWidth: "100%", display: "block" }}>
              {procedureLabel}
            </Typography.Text>
          </Tooltip>
        );
      },
    },
    {
      title: breakdownColumnTitle("Coverage"),
      key: "percentage_covered",
      dataIndex: "percentage_covered",
      width: 102,
      render: (text, record) => {
        if (recordsToEdit.includes(record.id)) {
          const currentEdit = editedData[record.id] || {};
          return (
            <Input
              disabled={isEditingProcedure}
              placeholder="Enter Coverage"
              className="edit-breakdown-procedure-cell"
              value={
                currentEdit.percentage_covered !== undefined ? currentEdit.percentage_covered : text !== null && text !== undefined ? `${text}` : "-"
              }
              onChange={(e) =>
                setEditedData((prevData) => ({
                  ...prevData,
                  [record.id]: { ...prevData[record.id], percentage_covered: e.target.value },
                }))
              }
            />
          );
        }

        return text !== null && text !== undefined ? `${text}%` : "-";
      },
    },
    {
      title: breakdownColumnTitle("Category"),
      key: "coverage_type",
      dataIndex: "coverage_type",
      width: 124,
      render: (text) => {
        if (!text) return "-";
        const colors = { Preventative: "green", Basic: "blue", Major: "gold", Orthodontics: "purple" };
        return (
          <Tag color={colors[text] || "default"} style={{ margin: 0 }}>
            {text}
          </Tag>
        );
      },
    },
    {
      title: breakdownColumnTitle("Frequency"),
      key: "max_occurrences",
      dataIndex: "max_occurrences",
      width: 115,
      render: (text, record) => {
        if (recordsToEdit.includes(record.id)) {
          const currentEdit = editedData[record.id] || {};
          const checkIsFrequencyUnlimited =
            currentEdit.isFrequencyUnlimited !== undefined ? currentEdit.isFrequencyUnlimited : record.coverage_duration_months === 999;

          return (
            <div className="edit-breakdown-procedure-cell" style={{ display: "flex", flexDirection: "column", alignItems: "start", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                <Input
                  disabled={isEditingProcedure || checkIsFrequencyUnlimited}
                  placeholder="Occurrences"
                  style={{ width: 50 }}
                  value={checkIsFrequencyUnlimited ? "" : currentEdit.max_occurrences !== undefined ? currentEdit.max_occurrences : text || ""}
                  onChange={(e) =>
                    setEditedData((prevData) => ({
                      ...prevData,
                      [record.id]: { ...prevData[record.id], max_occurrences: e.target.value },
                    }))
                  }
                />
                <span>every</span>
                <Input
                  disabled={isEditingProcedure || checkIsFrequencyUnlimited}
                  placeholder="Months"
                  style={{ width: 50 }}
                  value={
                    checkIsFrequencyUnlimited
                      ? ""
                      : currentEdit.coverage_duration_months !== undefined
                      ? currentEdit.coverage_duration_months
                      : record.coverage_duration_months || ""
                  }
                  onChange={(e) =>
                    setEditedData((prevData) => ({
                      ...prevData,
                      [record.id]: { ...prevData[record.id], coverage_duration_months: e.target.value },
                    }))
                  }
                />
                <span>months</span>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Checkbox
                  checked={checkIsFrequencyUnlimited}
                  onChange={(e) =>
                    setEditedData((prevData) => ({
                      ...prevData,
                      [record.id]: {
                        ...prevData[record.id],
                        isFrequencyUnlimited: e.target.checked,
                        max_occurrences: e.target.checked ? null : (prevData[record.id] && prevData[record.id].max_occurrences) || text,
                        coverage_duration_months: e.target.checked ? 999 : "",
                      },
                    }))
                  }
                />
                Unlimited
              </label>
            </div>
          );
        }

        const freqLine =
          record.max_occurrences && !record.coverage_duration_months
            ? String(record.max_occurrences)
            : record.coverage_duration_months === 999
              ? "Unlimited"
              : text
                ? `${text} every ${record.coverage_duration_months} months`
                : "-";
        return freqLine === "-" ? (
          "-"
        ) : (
          <Tooltip title={freqLine}>
            <Typography.Text ellipsis style={{ maxWidth: "100%", display: "block" }}>
              {freqLine}
            </Typography.Text>
          </Tooltip>
        );
      },
    },
    {
      title: breakdownColumnTitle("Notes"),
      key: "notes",
      dataIndex: "notes",
      width: 132,
      render: (text, record) => {
        if (recordsToEdit.includes(record.id)) {
          const currentEdit = editedData[record.id] || {};
          return (
            <Input
              disabled={isEditingProcedure}
              placeholder="Write note"
              className="edit-breakdown-procedure-cell"
              value={currentEdit.notes !== undefined ? currentEdit.notes : text ? text : "-"}
              onChange={(e) =>
                setEditedData((prevData) => ({
                  ...prevData,
                  [record.id]: { ...prevData[record.id], notes: e.target.value },
                }))
              }
            />
          );
        }

        return text ? (
          <Tooltip {...NOTE_TOOLTIP_PROPS} title={text}>
            <Typography.Text ellipsis={{ rows: 2 }} style={{ maxWidth: "100%", display: "block" }}>
              {text}
            </Typography.Text>
          </Tooltip>
        ) : (
          "-"
        );
      },
    },
    {
      title: breakdownColumnTitle("Action"),
      width: 96,
      render: (_, record) => {
        if (recordsToEdit.includes(record.id))
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* Confirm edit row button */}
              <Button
                onClick={() => handleEditBreakdownProcedure(record)}
                style={{ border: "none", backgroundColor: "transparent", width: 32, height: 32 }}
              >
                <img src={tick} alt="tick" />
              </Button>

              {/* Cancel Edit */}
              <Button onClick={() => handleCancelEdit(record)} style={{ border: "none", backgroundColor: "transparent", width: 32, height: 32 }}>
                <img src={close} alt="cancel" />
              </Button>
            </div>
          );

        // Edit row button
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Button
              onClick={() => handleEnterEditMode(record)}
              style={{ border: "none", backgroundColor: "transparent", width: 32, height: 32 }}
              disabled={isDeletingBreakdownProcedure}
            >
              <img src={edit} alt="edit" />
            </Button>

            {/* Cancel Delete */}
            <Button
              onClick={() => handleDeleteBreakdownProcedure(record)}
              style={{ border: "none", backgroundColor: "transparent", width: 32, height: 32 }}
              disabled={isDeletingBreakdownProcedure}
            >
              <img src={trash} alt="trash" />
            </Button>
          </div>
        );
      },
    },
  ];

  function handleChangeProcedureCode(recordId, value) {
    const selectedProcedure = allProcedures.find((procedure) => procedure.id === value);
    if (selectedProcedure) {
      setEditedData((prevData) => ({
        ...prevData,
        [recordId]: {
          ...prevData[recordId],
          procedure_code_id: selectedProcedure.id,
          procedure_code: selectedProcedure.procedure_code,
          procedure_description: selectedProcedure.procedure_code_description,
        },
      }));
    }
  }

  function handleEnterEditMode(record) {
    setRecordsToEdit((prevRecords) => [...prevRecords, record.id]);
    setEditedData((prevData) => ({
      ...prevData,
      [record.id]: {
        procedure_code_id: record.procedure_code_details.id || "",
        procedure_name: record.procedure_code_details.procedure_description || "",
        percentage_covered: record.percentage_covered !== null && record.percentage_covered !== undefined ? record.percentage_covered : "",
        overall_max: record.overall_max !== null && record.overall_max !== undefined ? record.overall_max : "",
        max_occurrences: record.max_occurrences || "",
        coverage_duration_months: record.coverage_duration_months || "",
        notes: record.notes || "",
        isUnlimited: record.overall_max === "9999999.99",
        isFrequencyUnlimited: record.coverage_duration_months === 999,
      },
    }));
  }

  function handleCancelEdit(record) {
    const newEditedData = { ...editedData };
    delete newEditedData[record.id];
    setEditedData(newEditedData);
    setRecordsToEdit((prevRecords) => prevRecords.filter((prevRecord) => prevRecord !== record.id));
  }

  async function handleEditBreakdownProcedure(record) {
    const currentEdit = editedData[record.id] || {};

    // Return an error if required fields are empty
    if (
      currentEdit &&
      (!currentEdit.procedure_code_id ||
        (!currentEdit.percentage_covered && currentEdit.percentage_covered === 0) ||
        (!currentEdit.isUnlimited && !currentEdit.overall_max) ||
        (!currentEdit.isFrequencyUnlimited && (!currentEdit.max_occurrences || !currentEdit.coverage_duration_months)) ||
        !currentEdit.notes)
    ) {
      message.error("Please fill up all required fields");
      return;
    }

    let max_occurrences = currentEdit.max_occurrences;
    let coverage_duration_months = currentEdit.coverage_duration_months;

    if (!currentEdit.isFrequencyUnlimited) {
      max_occurrences = parseInt(currentEdit.max_occurrences);
      coverage_duration_months = parseInt(currentEdit.coverage_duration_months);

      if (isNaN(max_occurrences) || isNaN(coverage_duration_months)) {
        message.error("Occurrences and months must be valid numbers");
        return;
      }
    } else {
      max_occurrences = null;
      coverage_duration_months = 999;
    }

    // Check if nothing changed, then return, otherwise send a request
    if (
      currentEdit.procedure_code_id === record.procedure_code &&
      currentEdit.percentage_covered === record.percentage_covered &&
      currentEdit.overall_max === record.overall_max &&
      currentEdit.max_occurrences === record.max_occurrences &&
      currentEdit.coverage_duration_months === record.coverage_duration_months &&
      currentEdit.notes === record.notes &&
      currentEdit.isUnlimited === (record.overall_max === "9999999.99") &&
      currentEdit.isFrequencyUnlimited === (record.coverage_duration_months === 999)
    ) {
      setRecordsToEdit((prevRecords) => prevRecords.filter((id) => id !== record.id));
      return;
    }

    // Return an error if coverage is not between 0 and 100
    if (Number(currentEdit.percentage_covered) < 0 || Number(currentEdit.percentage_covered) > 100) {
      message.error("Coverage must be between 0 and 100");
      return;
    }

    setIsEditingProcedure(true);

    try {
      const dataToSent = {
        id: record.id,
        procedure_code: currentEdit.procedure_code_id,
        percentage_covered: currentEdit.percentage_covered,
        overall_max: currentEdit.isUnlimited ? "9999999.99" : currentEdit.overall_max,
        max_occurrences,
        coverage_duration_months,
        notes: currentEdit.notes,
      };

      const response = await breakdownController.updateBreakdown(selectedBreakdown.id, dataToSent);

      if (response.status < 250) {
        setRecordsToEdit((prevRecords) => prevRecords.filter((id) => id !== record.id));
        setTableData((prevData) => {
          const filteredData = prevData.filter((data) => data.id !== record.id);
          return [...filteredData, response.json];
        });
      } else {
        // The server names the field it refused; "Update failed" did not.
        message.error(describeApiError(response, "Update failed."));
      }
    } catch (err) {
      message.error("Something went wrong while editing procedure");
      console.error(err);
    } finally {
      setIsEditingProcedure(false);
    }
  }

  async function handleDeleteBreakdownProcedure(record) {
    try {
      setIsDeletingBreakdownProcedure(true);

      const response = await breakdownController.deleteBreakdownProcedure(selectedBreakdown.id, record.id);

      if (response.status < 250) {
        message.success("Breakdown policy item deleted");
        getBreakdownsDetailsData();
      } else {
        message.error(describeApiError(response, "Couldn't delete that policy item."));
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong while deleting policy item");
    } finally {
      setIsDeletingBreakdownProcedure(false);
    }
  }

  // Scrape-attempted codes the portal returned nothing for — see the primary
  // copy (TreatmentPlans/component/Breakdown) for the full rationale.
  const availableRows = (tableData || []).filter((r) => !r.data_unavailable);
  const unavailableRows = (tableData || []).filter((r) => r.data_unavailable);

  return (
    <div style={{ borderRadius: 8 }} className="insurance-treatment-table-container">
      <Table
        rowKey="id"
        dataSource={availableRows}
        columns={patientInsurancesDetailsColumns}
        pagination={{ position: ["bottomCenter"] }}
        scroll={{ x: 885 }}
        tableLayout="fixed"
        className="breakdown-procedure-table"
      />

      {unavailableRows.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <button
            type="button"
            onClick={() => setShowUnavailable((x) => !x)}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: 8,
              padding: "10px 14px", borderRadius: 8, cursor: "pointer",
              border: "1px dashed #d9d4e7", background: "#faf9fd",
              color: "#6f6c80", fontSize: 13, textAlign: "left",
            }}
          >
            {showUnavailable ? <DownOutlined style={{ fontSize: 11 }} /> : <RightOutlined style={{ fontSize: 11 }} />}
            <span style={{ fontWeight: 600 }}>
              Data not available for {unavailableRows.length} procedure code{unavailableRows.length === 1 ? "" : "s"}
            </span>
            <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {unavailableRows.map((r) => (r.procedure_code_details && r.procedure_code_details.procedure_code) || "").filter(Boolean).join(", ")}
            </span>
          </button>
          {showUnavailable && (
            <>
              <div style={{ fontSize: 12, color: "#8a8798", margin: "8px 2px 6px" }}>
                The automatic fetch asked the portal for these and got nothing back.
                Recalculate retries them, or fill one in manually — it moves up once it has values.
              </div>
              <Table
                rowKey="id"
                dataSource={unavailableRows}
                columns={patientInsurancesDetailsColumns}
                pagination={false}
                scroll={{ x: 885 }}
                tableLayout="fixed"
                className="breakdown-procedure-table"
              />
            </>
          )}
        </div>
      )}

      <AddPatientModal showAddPatient={showAddPatient} setShowAddPatient={setShowAddPatient} />
    </div>
  );
}
